import { useState, useEffect, useCallback } from 'react';
import { Calculator, MessageSquare, Phone, Mail, StickyNote, X, GripVertical } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { id: 'neu', label: 'Neu', color: 'bg-blue-500' },
  { id: 'kontaktiert', label: 'Kontaktiert', color: 'bg-amber-500' },
  { id: 'wiedervorlage', label: 'Wiedervorlage', color: 'bg-orange-500' },
  { id: 'termin', label: 'Termin vereinbart', color: 'bg-violet-500' },
  { id: 'erledigt', label: 'Erledigt', color: 'bg-emerald-500' },
];

const TREATMENTS = { haar: 'Haar', bart: 'Bart', augenbrauen: 'Augenbrauen' };
const REFERRALS = { google: 'Google', instagram: 'Instagram', tiktok: 'TikTok', empfehlung: 'Empfehlung', werbetafel: 'Werbetafel', sonstiges: 'Sonstiges' };

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }); }
  catch { return ''; }
};

const LeadCard = ({ lead, isDark, onDragStart, onOpenNote, onOpenDetail, onToggleCalled }) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, lead)}
    onClick={() => onOpenDetail(lead)}
    data-testid={`lead-card-${lead.source}-${lead.id}`}
    className={`group rounded-xl border p-3 cursor-pointer select-none transition-colors ${
      isDark ? 'bg-[#161616] border-white/10 hover:border-white/25' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{lead.name || '—'}</span>
      <span
        className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${
          lead.source === 'price'
            ? 'bg-amber-500/15 text-amber-500'
            : 'bg-purple-500/15 text-purple-400'
        }`}
      >
        {lead.source === 'price' ? <Calculator className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
        {lead.source === 'price' ? 'Preisrechner' : 'Kontakt'}
      </span>
    </div>
    <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
      <span>{fmtDate(lead.created_at)}</span>
      {lead.treatment && <span>· {TREATMENTS[lead.treatment] || lead.treatment}</span>}
      {lead.referral_source && <span>· via {REFERRALS[lead.referral_source] || lead.referral_source}</span>}
    </div>
    {(lead.has_reply || lead.called) && (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {lead.has_reply && (
          <span data-testid={`lead-badge-replied-${lead.id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">
            <Mail className="w-3 h-3" /> Beantwortet
          </span>
        )}
        {lead.called && (
          <span data-testid={`lead-badge-called-${lead.id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-500">
            <Phone className="w-3 h-3" /> Angerufen{lead.called_at ? ` ${fmtDate(lead.called_at)}` : ''}
          </span>
        )}
      </div>
    )}
    {lead.pipeline_note && (
      <p className={`mt-1.5 text-[11px] line-clamp-2 ${isDark ? 'text-white/55' : 'text-gray-500'}`}>📝 {lead.pipeline_note}</p>
    )}
    <div className="mt-2 flex items-center gap-1.5">
      {lead.phone && (
        <a
          href={`tel:${String(lead.phone).replace(/\s/g, '')}`}
          data-testid={`lead-call-${lead.id}`}
          onClick={(e) => { e.stopPropagation(); if (!lead.called) onToggleCalled(lead, true); }}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/25 transition-colors"
        >
          <Phone className="w-3 h-3" /> Anrufen
        </a>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleCalled(lead, !lead.called); }}
        data-testid={`lead-toggle-called-${lead.id}`}
        title={lead.called ? 'Anruf-Markierung entfernen' : 'Als angerufen markieren'}
        className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full transition-colors ${
          lead.called
            ? 'bg-sky-500/20 text-sky-400'
            : isDark ? 'bg-white/5 text-white/60 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}
      >
        📞 {lead.called ? '✓' : 'markieren'}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onOpenNote(lead); }}
        data-testid={`lead-note-btn-${lead.id}`}
        className={`ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full transition-colors ${
          isDark ? 'bg-white/5 text-white/60 hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }`}
      >
        <StickyNote className="w-3 h-3" /> Notiz
      </button>
    </div>
  </div>
);

export default function LeadBoard() {
  const { isDark } = useTheme();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [noteLead, setNoteLead] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [detailLead, setDetailLead] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const token = localStorage.getItem('adminToken') || '';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/leads`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setLeads(data.leads || []);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updatePipeline = async (lead, patch) => {
    setLeads((ls) => ls.map((l) => (l.id === lead.id && l.source === lead.source ? { ...l, ...patch } : l)));
    await fetch(`${API}/api/admin/leads/${lead.source}/${lead.id}/pipeline`, {
      method: 'PUT', headers, body: JSON.stringify(patch),
    });
  };

  const onDragStart = (e, lead) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: lead.id, source: lead.source }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (e, colId) => {
    e.preventDefault();
    setDragOverCol(null);
    try {
      const { id, source } = JSON.parse(e.dataTransfer.getData('text/plain'));
      const lead = leads.find((l) => l.id === id && l.source === source);
      if (lead && lead.pipeline_status !== colId) updatePipeline(lead, { pipeline_status: colId });
    } catch { /* ignore */ }
  };

  const saveNote = async () => {
    await updatePipeline(noteLead, { pipeline_note: noteText });
    setNoteLead(null);
  };

  const toggleCalled = (lead, called) => updatePipeline(lead, { called });

  const openDetail = (lead) => {
    setDetailLead(lead);
    setReplySubject(`Ihre Anfrage - Dion Hair Clinic`);
    setReplyBody('');
  };

  const generateReply = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/price-inquiries/${detailLead.id}/generate-reply`, {
        method: 'POST', headers,
        body: JSON.stringify({ inquiry_id: detailLead.id, bullet_points: '', include_patient_data: true }),
      });
      const data = await res.json();
      if (data.success) {
        setReplySubject(data.subject || replySubject);
        setReplyBody(data.body || '');
      } else {
        alert(data.detail || 'Generierung fehlgeschlagen');
      }
    } catch { alert('Generierung fehlgeschlagen'); }
    finally { setGenerating(false); }
  };

  const sendReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/price-inquiries/${detailLead.id}/send-reply`, {
        method: 'POST', headers,
        body: JSON.stringify({ inquiry_id: detailLead.id, subject: replySubject, body: replyBody, attachment_ids: [] }),
      });
      const data = await res.json();
      if (res.ok) {
        setLeads((ls) => ls.map((l) => (l.id === detailLead.id && l.source === 'price' ? { ...l, has_reply: true, status: 'replied' } : l)));
        setDetailLead(null);
      } else {
        alert(data.detail || 'Versand fehlgeschlagen');
      }
    } catch { alert('Versand fehlgeschlagen'); }
    finally { setSending(false); }
  };

  const openInModule = (lead) => {
    const section = lead.source === 'price' ? 'inquiries' : 'contacts';
    window.location.href = `/admin?section=${section}&inquiry=${lead.id}`;
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`} data-testid="lead-board">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Lead-Board</h1>
        <p className={`text-sm mt-1 mb-6 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
          Preisrechner- & Kontaktanfragen per Drag & Drop einsortieren
        </p>

        {loading ? (
          <p className={isDark ? 'text-white/40' : 'text-gray-400'}>Lade Leads…</p>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-6 items-start">
            {COLUMNS.map((col) => {
              const items = leads.filter((l) => (l.pipeline_status || 'neu') === col.id);
              return (
                <div
                  key={col.id}
                  onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
                  onDragLeave={() => setDragOverCol((c) => (c === col.id ? null : c))}
                  onDrop={(e) => onDrop(e, col.id)}
                  data-testid={`lead-column-${col.id}`}
                  className={`w-[290px] shrink-0 rounded-2xl border p-3 transition-colors ${
                    dragOverCol === col.id
                      ? 'border-[#7BA7C2] bg-[#7BA7C2]/5'
                      : isDark ? 'border-white/10 bg-[#101010]' : 'border-gray-200 bg-gray-100/60'
                  }`}
                >
                  <div className="flex items-center gap-2 px-1 mb-3">
                    <span className={`w-2.5 h-2.5 rounded-full ${col.color}`} />
                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{col.label}</span>
                    <span
                      data-testid={`lead-count-${col.id}`}
                      className={`ml-auto text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-white/10 text-white/60' : 'bg-white text-gray-500'}`}
                    >
                      {items.length}
                    </span>
                  </div>
                  <div className="space-y-2 min-h-[60px]">
                    {items.map((lead) => (
                      <LeadCard
                        key={`${lead.source}-${lead.id}`}
                        lead={lead}
                        isDark={isDark}
                        onDragStart={onDragStart}
                        onOpenDetail={openDetail}
                        onToggleCalled={toggleCalled}
                        onOpenNote={(l) => { setNoteLead(l); setNoteText(l.pipeline_note || ''); }}
                      />
                    ))}
                    {items.length === 0 && (
                      <div className={`text-xs text-center py-6 rounded-xl border border-dashed ${isDark ? 'border-white/10 text-white/25' : 'border-gray-300 text-gray-400'}`}>
                        <GripVertical className="w-4 h-4 mx-auto mb-1 opacity-50" />
                        Karten hierher ziehen
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {detailLead && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 overflow-y-auto" data-testid="lead-detail-modal">
          <div className={`w-full max-w-2xl my-8 rounded-2xl border p-6 ${isDark ? 'bg-[#141414] border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{detailLead.name}</h3>
                <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {detailLead.source === 'price' ? 'Preisrechner-Anfrage' : 'Kontaktanfrage'} · {fmtDate(detailLead.created_at)}
                </p>
              </div>
              <button onClick={() => setDetailLead(null)} data-testid="lead-detail-close" className={isDark ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-600'}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm rounded-xl border p-4 ${isDark ? 'border-white/10 bg-white/[0.03] text-white/80' : 'border-gray-100 bg-gray-50 text-gray-700'}`}>
              {[
                ['E-Mail', detailLead.email],
                ['Telefon', detailLead.phone || '-'],
                ['Behandlung', TREATMENTS[detailLead.treatment] || detailLead.treatment || '-'],
                ['Methode', ({ classic: 'Klassisch', dhi: 'DHI (ohne Rasur)', longhair: 'Long Hair FUE' })[detailLead.method] || detailLead.method || '-'],
                ['Alter', detailLead.age || '-'],
                ['Herkunft', REFERRALS[detailLead.referral_source] || detailLead.referral_source || '-'],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{k}</div>
                  <div className="truncate">{v}</div>
                </div>
              ))}
              {detailLead.message && (
                <div className="col-span-2 sm:col-span-3">
                  <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Nachricht</div>
                  <div className="whitespace-pre-wrap">{detailLead.message}</div>
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {detailLead.phone && (
                <a
                  href={`tel:${String(detailLead.phone).replace(/\s/g, '')}`}
                  onClick={() => { if (!detailLead.called) toggleCalled(detailLead, true); }}
                  className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/25 transition-colors"
                >
                  <Phone className="w-4 h-4" /> Anrufen
                </a>
              )}
              <button
                onClick={() => openInModule(detailLead)}
                data-testid="lead-open-module"
                className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full transition-colors ${isDark ? 'bg-white/5 text-white/70 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Im Anfrage-Modul öffnen →
              </button>
            </div>

            {detailLead.source === 'price' && (
              <div className={`mt-5 rounded-xl border p-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Antwort senden {detailLead.has_reply && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">bereits beantwortet</span>}</h4>
                  <button
                    onClick={generateReply}
                    disabled={generating}
                    data-testid="lead-generate-reply"
                    className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] text-white disabled:opacity-50"
                  >
                    {generating ? 'Generiere…' : '✨ KI-Antwort generieren'}
                  </button>
                </div>
                <input
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  data-testid="lead-reply-subject"
                  className={`w-full mb-2 rounded-lg border px-3 py-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                />
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  rows={7}
                  placeholder="Antworttext – oder per KI generieren…"
                  data-testid="lead-reply-body"
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-white border-gray-200 text-gray-900'}`}
                />
                <button
                  onClick={sendReply}
                  disabled={sending || !replyBody.trim()}
                  data-testid="lead-send-reply"
                  className="mt-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] text-white text-sm font-medium disabled:opacity-40"
                >
                  {sending ? 'Sende…' : 'Antwort per E-Mail senden'}
                </button>
              </div>
            )}
            {detailLead.source === 'contact' && detailLead.email && (
              <a
                href={`mailto:${detailLead.email}?subject=Ihre Anfrage - Dion Hair Clinic`}
                className={`mt-4 block text-center py-2.5 rounded-xl border text-sm ${isDark ? 'border-white/15 text-white/80 hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
              >
                <Mail className="w-4 h-4 inline mr-1.5" /> Per E-Mail antworten
              </a>
            )}
          </div>
        </div>
      )}

      {noteLead && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="lead-note-modal">
          <div className={`w-full max-w-md rounded-2xl border p-5 ${isDark ? 'bg-[#141414] border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Notiz zu {noteLead.name}</h3>
              <button onClick={() => setNoteLead(null)} data-testid="lead-note-close" className={isDark ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-600'}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
              placeholder="z.B. AB besprochen, Rückruf Montag 14 Uhr…"
              data-testid="lead-note-textarea"
              className={`w-full rounded-xl border p-3 text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30' : 'bg-white border-gray-200 text-gray-900'}`}
            />
            <button
              onClick={saveNote}
              data-testid="lead-note-save"
              className="mt-3 w-full py-2.5 rounded-xl bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Notiz speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
